import type {
  BulkAssignInput,
  CreateTrainingEnrollmentInput,
  RecordAttendanceInput,
} from '@lms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export function useMyEnrollments() {
  return useQuery({
    queryKey: ['my-enrollments'],
    queryFn: () => api.get('/api/training-enrollments/my'),
  })
}

export function useSubordinateEnrollments() {
  return useQuery({
    queryKey: ['subordinate-enrollments'],
    queryFn: () => api.get('/api/training-enrollments/subordinates'),
  })
}

/** GET /api/training-enrollments の一覧要素（管理者向け） */
export interface TrainingEnrollmentListItem {
  id: string
  status: string
  userName: string | null
  userEmail: string | null
  trainingTitle: string | null
}

/** API 1回あたりの上限（サーバー側の getPaginationParams と同じ） */
const ENROLLMENT_PAGE_SIZE = 100

/** 研修の受講者一覧（管理者のみ）。100件を超える場合は全ページを取得して結合する */
export function useTrainingEnrollments(trainingId: string) {
  return useQuery({
    queryKey: ['training-enrollments', trainingId],
    queryFn: async () => {
      const all: TrainingEnrollmentListItem[] = []

      for (let page = 1; ; page++) {
        const items = await api.get<TrainingEnrollmentListItem[]>('/api/training-enrollments', {
          trainingId,
          limit: ENROLLMENT_PAGE_SIZE,
          page,
        })
        all.push(...items)
        // 上限未満なら最後のページ
        if (items.length < ENROLLMENT_PAGE_SIZE) return all
      }
    },
    enabled: !!trainingId,
  })
}

/** 出欠記録（enrollment_attendances の1行） */
export interface EnrollmentAttendance {
  id: string
  enrollmentId: string
  scheduleId: string
  attended: boolean
}

/** 研修の全受講者の出欠（出欠管理画面用。研修運営以上） */
export function useTrainingAttendances(trainingId: string) {
  return useQuery({
    queryKey: ['training-attendances', trainingId],
    queryFn: () =>
      api.get<EnrollmentAttendance[]>(
        `/api/training-enrollments/training/${trainingId}/attendance`,
      ),
    enabled: !!trainingId,
  })
}

export function useEnrollmentAttendances(enrollmentId: string) {
  return useQuery({
    queryKey: ['enrollment-attendances', enrollmentId],
    queryFn: () => api.get(`/api/training-enrollments/${enrollmentId}/attendance`),
    enabled: !!enrollmentId,
  })
}

export function useEnrollSelf() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTrainingEnrollmentInput) =>
      api.post('/api/training-enrollments/enroll', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-enrollments'] })
      qc.invalidateQueries({ queryKey: ['trainings'] })
    },
  })
}

export function useAssignTrainees() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BulkAssignInput) => api.post('/api/training-enrollments/assign', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training-enrollments'] })
      qc.invalidateQueries({ queryKey: ['trainings'] })
    },
  })
}

export function useCancelEnrollment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (enrollmentId: string) =>
      api.post(`/api/training-enrollments/${enrollmentId}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-enrollments'] })
      qc.invalidateQueries({ queryKey: ['trainings'] })
    },
  })
}

export function useRecordAttendance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ enrollmentId, data }: { enrollmentId: string; data: RecordAttendanceInput }) =>
      api.post(`/api/training-enrollments/${enrollmentId}/attendance`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollment-attendances'] })
      qc.invalidateQueries({ queryKey: ['training-attendances'] })
    },
  })
}
